import { useEffect, useState } from "react";
import api from "@/lib/api";
import useIsMobile from "@/lib/useIsMobile";
import { money } from "@/lib/ui";
import { Loader2, Crosshair, ArrowUp, ArrowDown } from "lucide-react";

// NFR Cutline: #15 IN vs #16 OUT per PRCA event, with the dollar gap. Tightest first.
export default function NfrCutline() {
  const m = useIsMobile();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/prca/cutline").then((r) => setData(r.data)).catch(() => setData({ events: [] })); }, []);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";

  return (
    <div className={page} data-testid="cutline-page">
      <div className={`px-4 sm:px-6 py-5 border-b ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <div className="flex items-center gap-2">
          <Crosshair className="w-6 h-6 text-[#C8000F]" />
          <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>NFR Cutline</h1>
        </div>
        <p className={`text-sm mt-1 ${sub}`}>Fifteen spots. Everyone else drives home. The last ticket in and the first one chasing, by the dollar{data?.year ? ` · ${data.year} season` : ""}.</p>
      </div>

      <div className="p-4 sm:p-6 max-w-5xl">
        {data === null ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : data.events.length === 0 ? (
          <div className={`text-center py-16 ${sub}`}>Standings aren't available right now — check back shortly.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.events.map((e, i) => {
              const tight = i === 0 && e.gap != null;
              return (
                <div key={e.abbr} data-testid={`cutline-card-${e.abbr}`}
                  className={`rounded-xl border p-4 ${card} ${tight ? "ring-2 ring-[#C8000F]" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div className={`font-display font-bold text-sm ${title}`}>{e.event}</div>
                    {tight && <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#C8000F] text-white">Tightest</span>}
                  </div>

                  <div className="mt-3 space-y-2">
                    <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${m ? "bg-[#16A34A]/10" : "bg-[#EAF3EC]"}`} data-testid={`cutline-in-${e.abbr}`}>
                      <div className="min-w-0 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-[#166534] text-white text-[10px] font-extrabold flex items-center justify-center shrink-0">15</span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#166534]"><ArrowUp className="w-3 h-3" /> In</div>
                          <div className={`text-sm font-semibold truncate ${title}`}>{e.in.name}</div>
                        </div>
                      </div>
                      <div className={`text-sm font-bold shrink-0 ${title}`}>{money(e.in.earnings)}</div>
                    </div>

                    {e.out ? (
                      <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${m ? "bg-white/5" : "bg-[#F7F8FA]"}`} data-testid={`cutline-out-${e.abbr}`}>
                        <div className="min-w-0 flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full text-[10px] font-extrabold flex items-center justify-center shrink-0 ${m ? "bg-white/10 text-slate-300" : "bg-slate-200 text-slate-600"}`}>16</span>
                          <div className="min-w-0">
                            <div className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide ${sub}`}><ArrowDown className="w-3 h-3" /> Out</div>
                            <div className={`text-sm font-semibold truncate ${sub}`}>{e.out.name}</div>
                          </div>
                        </div>
                        <div className={`text-sm font-bold shrink-0 ${sub}`}>{money(e.out.earnings)}</div>
                      </div>
                    ) : (
                      <div className={`text-xs px-3 py-2 ${sub}`}>No #16 posted.</div>
                    )}
                  </div>

                  {e.gap != null && (
                    <div className={`mt-3 text-center text-sm font-bold ${tight ? "text-[#C8000F]" : title}`} data-testid={`cutline-gap-${e.abbr}`}>
                      {money(e.gap)} <span className={`font-semibold ${sub}`}>separates the cut</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
